import { useState } from 'react';
import { Camera } from '../../cameras/api/types';
import { useSetupCameraMutation, useConfigureRtspMutation, useRtspNowMutation } from '../api/livestreamingApi';

interface SetupData {
  quality: string;
  duration: number;
  includeAudio: boolean;
  scheduleHours: number[];
  scheduleDays: string[];
}

export const useLivestreamingSetup = () => {
  // API mutations
  const [setupCamera] = useSetupCameraMutation();
  const [configureRtsp] = useConfigureRtspMutation();
  const [rtspNow] = useRtspNowMutation();
  
  // State
  const [setupData, setSetupData] = useState<SetupData>({
    quality: 'SD', // Maps to rtspPath: SD = /12, HD = /11
    duration: 15, // Default to 15 seconds (available option)
    includeAudio: false,
    scheduleHours: [9, 10, 11, 12, 13, 14, 15, 16, 17],
    scheduleDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']
  });

  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [setupStep, setSetupStep] = useState<'select' | 'configure' | 'submitted'>('select');
  const [isSettingUpCamera, setIsSettingUpCamera] = useState(false);
  const [cameraSetupComplete, setCameraSetupComplete] = useState(false);
  const [setupProgress, setSetupProgress] = useState(0);
  const [isConfiguringRtsp, setIsConfiguringRtsp] = useState(false);
  const [rtspConfigProgress, setRtspConfigProgress] = useState(0);
  const [isStreamingNow, setIsStreamingNow] = useState(false);
  const [videoS3Key, setVideoS3Key] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string | number | boolean | number[] | string[]) => {
    setSetupData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createProgressInterval = (
    setProgress: (value: number | ((prev: number) => number)) => void,
    duration: number = 15
  ) => {
    return setInterval(() => {
      setProgress(prev => {
        const increment = (95 / (duration * 10)); // 10 updates per second
        return Math.min(prev + increment, 95);
      });
    }, 100); // Update every 100ms
  };

  const handleCameraSetup = async (): Promise<{ success: boolean; message?: string }> => {
    if (!selectedCamera) return { success: false, message: 'No camera selected' };
    
    setIsSettingUpCamera(true);
    setSetupProgress(0);
    
    const progressInterval = createProgressInterval(setSetupProgress);
    
    try {
      const requestBody = {
        sim_number: selectedCamera.SIM_ICCID,
        ports: [554] // Always use port 554 for RTSP
      };
      
      
      const result = await setupCamera(requestBody).unwrap();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to setup camera');
      }
      
      // Complete the progress bar
      clearInterval(progressInterval);
      setSetupProgress(100);
      
      // Small delay to show 100% completion, then start preview stream
      setTimeout(async () => {
        setCameraSetupComplete(true);
        setSetupStep('configure');
        
        // Trigger preview RTSP stream in background for success page
        try {
          await handlePreviewStream();
        } catch (error) {
          console.error('Error starting preview RTSP streaming:', error);
        }
      }, 500);
      
      return { success: true, message: 'Camera setup successful! Your camera has been configured for livestreaming.' };
      
    } catch (error: any) {
      clearInterval(progressInterval);
      setSetupProgress(0);
      console.error('Error setting up camera:', error);
      return { 
        success: false, 
        message: `Failed to setup camera: ${error.data?.message || error.message || error}. Please try again or contact support.` 
      };
    } finally {
      setIsSettingUpCamera(false);
    }
  };

  const handleRtspConfiguration = async (): Promise<{ success: boolean; message?: string }> => {
    if (!selectedCamera) return { success: false, message: 'No camera selected' };
    
    setIsConfiguringRtsp(true);
    setRtspConfigProgress(0);
    
    const progressInterval = createProgressInterval(setRtspConfigProgress, 3); // Much faster - 3 seconds total
    
    try {
      const scheduleJson = JSON.stringify({
        hours: setupData.scheduleHours,
        days: setupData.scheduleDays
      });
      
      // Map quality to RTSP path
      const rtspPath = setupData.quality === 'HD' ? '/11' : '/12';
      
      const requestBody = {
        camera_id: selectedCamera.SIM_ICCID,
        rtsp_port: 554, // Always use 554 as default
        rtsp_path: rtspPath,
        duration: setupData.duration,
        include_audio: setupData.includeAudio,
        is_active: true, // Always send as true for livestreaming management
        schedule: scheduleJson
      };
      
      
      const result = await configureRtsp(requestBody).unwrap();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to configure RTSP');
      }
      
      // Complete the progress bar
      clearInterval(progressInterval);
      setRtspConfigProgress(100);
      
      // Small delay to show 100% completion, then show success page
      setTimeout(() => {
        setSetupStep('submitted');
      }, 300); // Reduced delay for faster transition
      
      return { success: true, message: 'Livestream configuration successful! Your camera has been configured with your settings.' };
      
    } catch (error: any) {
      clearInterval(progressInterval);
      setRtspConfigProgress(0);
      console.error('Error configuring RTSP:', error);
      return { 
        success: false, 
        message: `Failed to configure RTSP: ${error.data?.message || error.message || error}. Please try again or contact support.` 
      };
    } finally {
      setIsConfiguringRtsp(false);
    }
  };

  const handleRtspNow = async (): Promise<{ success: boolean; message?: string }> => {
    if (!selectedCamera) return { success: false, message: 'No camera selected' };
    
    setIsStreamingNow(true);
    
    try {
      // Map quality to RTSP path
      const rtspPath = setupData.quality === 'HD' ? '/11' : '/12';
      
      const requestBody = {
        camera_id: selectedCamera.SIM_ICCID,
        rtsp_port: 554, // Always use 554 as default
        rtsp_path: rtspPath,
        duration: setupData.duration,
        include_audio: setupData.includeAudio
      };
      
      
      const result = await rtspNow(requestBody).unwrap();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to start immediate streaming');
      }
      
      // Store the S3 key for video playback
      if (result.data?.s3_key) {
        setVideoS3Key(result.data.s3_key);
      }
      
      return { success: true };
      
    } catch (error: any) {
      console.error('Error starting immediate RTSP streaming:', error);
      return { 
        success: false, 
        message: `Failed to start immediate streaming: ${error.data?.message || error.message || error}. Please try again or contact support.` 
      };
    } finally {
      setIsStreamingNow(false);
    }
  };

  const handlePreviewStream = async (): Promise<{ success: boolean; message?: string }> => {
    if (!selectedCamera) return { success: false, message: 'No camera selected' };
    
    
    try {
      // Use SD quality and 5 seconds duration for preview, no audio to keep it simple
      const requestBody = {
        camera_id: selectedCamera.SIM_ICCID,
        rtsp_port: 554,
        rtsp_path: '/12', // SD quality for preview
        duration: 5, // Fixed 5 seconds for preview
        include_audio: false // No audio for preview to reduce complexity
      };
      
      
      const result = await rtspNow(requestBody).unwrap();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to start preview streaming');
      }
      
      // Store the S3 key for video playback on success page
      if (result.data?.s3_key) {
        setVideoS3Key(result.data.s3_key);
      }
      
      return { success: true };
      
    } catch (error: any) {
      console.error('Error starting preview RTSP streaming:', error);
      return { 
        success: false, 
        message: `Failed to start preview streaming: ${error.data?.message || error.message || error}. This won't affect your setup.` 
      };
    }
  };

  return {
    // State
    setupData,
    selectedCamera,
    setupStep,
    isSettingUpCamera,
    cameraSetupComplete,
    setupProgress,
    isConfiguringRtsp,
    rtspConfigProgress,
    isStreamingNow,
    videoS3Key,

    // Actions
    setSelectedCamera,
    handleInputChange,
    handleCameraSetup,
    handleRtspConfiguration,
    handleRtspNow
  };
};
